import type { ReactNode } from 'react';
import type { IFilterItemConfig } from './types';

export interface IFilterPreviewContext {
  [key: string]: unknown;
}

/** 渲染器接收的标准 Props */
export interface IFilterRenderProps<T = any> {
  /** React key */
  key: string;
  /** 当前语言环境 */
  locale: string;
  /** 当前筛选器完整配置项 */
  item?: IFilterItemConfig;
  /** 筛选器实例的 field 名 */
  field: string;
  /** 显示标签 */
  label?: string;
  /** 当前值 */
  value: T;
  /** 值变更回调 */
  onChange: (value: T) => void;
  /** 实例级别的额外控件 props */
  [key: string]: any;
}

/** 单个筛选器类型的注册条目 */
export interface IFilterTypeEntry {
  /** 渲染函数 */
  render: (props: IFilterRenderProps) => ReactNode;
  /** 可选：保存弹窗中的值预览渲染 */
  renderPreview?: (item: IFilterItemConfig, value: any) => ReactNode;
  /** 可选：按预览上下文生成值预览渲染 */
  getRenderPreview?: (ctx: IFilterPreviewContext) => IFilterTypeEntry['renderPreview'];
  /** 可选：控件级值归一化 */
  normalizeValue?: (item: IFilterItemConfig, value: unknown) => unknown;
}

class FilterTypeRegistry {
  private entries = new Map<string, IFilterTypeEntry>();

  /** 检查是否已注册 */
  has(type: string): boolean {
    return this.entries.has(type);
  }

  /** 注册单个筛选器类型 */
  register(type: string, entry: IFilterTypeEntry): void {
    this.warnDuplicateRegistration(type, entry);
    this.entries.set(type, entry);
  }

  /** 批量注册 */
  registerAll(map: Record<string, IFilterTypeEntry>): void {
    Object.entries(map).forEach(([type, entry]) => {
      this.register(type, entry);
    });
  }

  /** 获取筛选器类型注册条目 */
  get(type: string): IFilterTypeEntry | undefined {
    return this.entries.get(type);
  }

  /** 获取带预览上下文的注册条目 */
  getResolved(type: string, ctx: IFilterPreviewContext = {}): IFilterTypeEntry | undefined {
    const entry = this.entries.get(type);

    if (!entry) {
      return undefined;
    }

    const renderPreview = entry.getRenderPreview?.(ctx);

    if (!renderPreview) {
      return entry;
    }

    return {
      ...entry,
      renderPreview,
    };
  }

  private warnDuplicateRegistration(type: string, nextEntry: IFilterTypeEntry): void {
    if (process.env.NODE_ENV === 'production') {
      return;
    }

    const currentEntry = this.entries.get(type);

    if (currentEntry && currentEntry !== nextEntry) {
      console.debug('[dbi] [Filter] duplicate filter type registration', type);
    }
  }
}

export const filterTypeRegistry = new FilterTypeRegistry();
